import React, { useEffect, useState } from "react";
import { Button, Card, Form, Input, Modal, Spin } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { DynamicTagsEntries } from "./dynamic-tags/dynamic-tags-entries";
import {
  getDynamicTags,
  createDynamicTag,
  updateDynamicTag,
  DYNAMIC_TAGS_QUERY,
  deleteDynamicTag,
} from "./dynamic-tags/dynamic-tags-graphql";
import { DynamicTagConditionAndOr, DynamicTagContentType } from "~/gql/graphql";
import { useQuery } from "@apollo/client";
import { ORGANISATIONAL_UNITS_QUERY } from "~/graphql/query/organisational-units-query";

export type Condition = {
  condition?: DynamicTagConditionAndOr | null;
  organisationalUnitId?: number[] | null;
  officeId?: number | null;
};

export type FormValues = {
  tags: {
    id?: number;
    name: string;
    token: string;
    entries: {
      id?: number;
      name: string;
      contents: string;
      imageFileName?: string;
      order: number;
      contentType: DynamicTagContentType;
      condition?: Condition;
    }[];
  }[];
};

const findPathForOrganisationalUnit = (paOptions: any[]) => (organisationalUnitId: number) => {
  for (const parent of paOptions) {
    if (parent.value === organisationalUnitId) {
      return [parent.value];
    }
    for (const child of parent.children) {
      if (child.value === organisationalUnitId) {
        return [parent.value, child.value];
      }
    }
  }
  return [];
};

export const DynamicTagsPage = () => {
  const [form] = Form.useForm<FormValues>();

  const [selectedTagIndex, setSelectedTagIndex] = useState<number | null>(null);
  const [showEntriesModal, setShowEntriesModal] = useState(false);
  const [isAddTagModalVisible, setIsAddTagModalVisible] = useState(false);
  const [tempTagName, setTempTagName] = useState("");

  const { data } = getDynamicTags();
  const [createTag] = createDynamicTag();
  const [updateTag, { loading: updateTagLoading }] = updateDynamicTag();
  const [deleteTag] = deleteDynamicTag();

  const { data: practiceAreas } = useQuery(ORGANISATIONAL_UNITS_QUERY);

  const paOptions =
    practiceAreas?.organisationalUnits
      .filter((x) => !x.parent?.id)
      .map((pa) => {
        return {
          value: pa.id,
          label: pa.name,
          children: pa.children?.map((y) => ({ value: y.id, label: y.name })) || [],
        };
      }) || [];

  const findPathForOrganisationalUnitFunc = findPathForOrganisationalUnit(paOptions);

  useEffect(() => {
    form.setFieldsValue({
      tags: data?.dynamicTags.map((tag) => ({
        id: tag.id,
        name: tag.name,
        token: tag.token,
        entries: tag.dynamicTagEntries.map((entry) => ({
          id: entry.id,
          contents: entry.contents,
          condition: {
            condition: entry.condition?.condition,
            organisationalUnitId: entry.condition?.organisationalUnitId
              ? findPathForOrganisationalUnitFunc(entry.condition.organisationalUnitId)
              : null,
            officeId: entry.condition?.officeId,
          },
          contentType: entry.contentType,
        })),
      })),
    });
  }, [data, paOptions]);

  const handleTagClick = (index: number) => {
    setShowEntriesModal(true);
    setSelectedTagIndex(index);
  };

  const handleModalClose = () => {
    setShowEntriesModal(false);
    setSelectedTagIndex(null);
  };

  const handleEntriesOk = () => {
    form
      .validateFields()
      .then((values) => {
        // Check if values changed

        if (selectedTagIndex === null) {
          return;
        }

        const tag = values.tags[selectedTagIndex];

        if (!tag.id) {
          return;
        }

        const toCreate = tag.entries.filter((entry) => !entry.id);

        const toUpdate = tag.entries.filter((entry) => entry.id);

        const existingEntries = data?.dynamicTags[selectedTagIndex].dynamicTagEntries.map(
          (entry) => entry.id
        );

        const toDelete = existingEntries?.filter(
          (id) => !toUpdate.some((entry) => entry.id === id)
        );

        updateTag({
          onCompleted: () => {
            setSelectedTagIndex(null);
            setShowEntriesModal(false);
          },
          refetchQueries: [DYNAMIC_TAGS_QUERY],
          variables: {
            updateDynamicTagId: tag.id!,
            update: {
              name: { set: tag.name },
              token: { set: tag.token },
              dynamicTagEntries: {
                create: toCreate.map((entry) => ({
                  contents: entry.contents,
                  contentType: entry.contentType,
                  order: tag.entries.indexOf(entry),
                  condition: {
                    create: {
                      condition: entry.condition?.condition,
                      organisationalUnitId: Array.isArray(entry.condition?.organisationalUnitId)
                        ? entry.condition?.organisationalUnitId[
                            entry.condition?.organisationalUnitId.length - 1
                          ]
                        : entry.condition?.organisationalUnitId,
                      officeId: entry.condition?.officeId,
                    },
                  },
                })),
                delete: toDelete?.map((id) => ({ id })),
                update: toUpdate.map((entry) => {
                  let organisationalUnitId = null;

                  if (
                    entry.condition?.organisationalUnitId?.length &&
                    entry.condition?.organisationalUnitId.length > 0
                  ) {
                    organisationalUnitId = Array.isArray(entry.condition?.organisationalUnitId)
                      ? entry.condition?.organisationalUnitId[
                          entry.condition?.organisationalUnitId.length - 1
                        ]
                      : entry.condition?.organisationalUnitId;
                  }

                  return {
                    where: { id: entry.id },
                    data: {
                      order: { set: tag.entries.indexOf(entry) },
                      contents: { set: entry.contents },
                      contentType: { set: entry.contentType },

                      condition: {
                        update: {
                          data: {
                            condition: {
                              set: entry.condition?.condition ?? null,
                            },
                            organisationalUnitId: {
                              set: organisationalUnitId,
                            },
                            officeId: {
                              set: entry.condition?.officeId ?? null,
                            },
                          },
                        },
                      },
                    },
                  };
                }),
              },
            },
          },
        });
      })
      .catch((error) => {
        console.error(error);
      });
  };

  return (
    <div>
      <Form form={form} layout="vertical">
        <Form.List name="tags">
          {(tags, { add: addTag, remove: removeTag }) => (
            <>
              <Button
                style={{ marginBottom: "16px" }}
                icon={<PlusOutlined />}
                onClick={() => setIsAddTagModalVisible(true)}
              >
                Add Tag
              </Button>
              <Card>
                {tags.map((tag, tagIndex) => (
                  <div
                    key={tag.key}
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #f0f0f0",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span style={{ flex: 1 }}>
                      <Form.Item
                        name={[tag.name, "name"]}
                        style={{ margin: 0 }}
                        getValueProps={(value) => ({ children: value })}
                      >
                        <span></span>
                      </Form.Item>
                    </span>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <Button type="primary" onClick={() => handleTagClick(tagIndex)}>
                        Edit
                      </Button>
                      <Button
                        type="text"
                        danger
                        onClick={() => {
                          Modal.confirm({
                            title: "Are you sure you want to delete this tag?",
                            onOk: () => {
                              const formValues = form.getFieldsValue();
                              const target = formValues.tags[tagIndex];

                              if (!target.id) {
                                alert("Tag not found");
                                return;
                              }
                              deleteTag({
                                variables: {
                                  deleteDynamicTagId: target.id,
                                },
                                refetchQueries: [DYNAMIC_TAGS_QUERY],
                              });
                              removeTag(tagIndex);
                            },
                          });
                        }}
                        icon={<DeleteOutlined />}
                      />
                    </div>
                  </div>
                ))}
              </Card>

              <Modal
                title="Tag Details"
                open={showEntriesModal}
                onCancel={handleModalClose}
                width={1200}
                style={{ height: "80vh" }}
                okButtonProps={{ disabled: !form.isFieldsTouched() }}
                onOk={() => {
                  // setShowEntriesModal(false);
                  handleEntriesOk();
                }}
              >
                {updateTagLoading && (
                  <div style={{ display: "flex", justifyContent: "center", margin: "16px" }}>
                    <Spin size="large" />
                  </div>
                )}
                {!updateTagLoading && selectedTagIndex !== null && tags[selectedTagIndex] && (
                  <div>
                    <div style={{ display: "flex", gap: "16px", marginBottom: "16px" }}>
                      <Form.Item
                        label="Name"
                        name={[tags[selectedTagIndex].name, "name"]}
                        style={{ margin: 0, flex: "1" }}
                      >
                        <Input placeholder="Example Merge Tag" />
                      </Form.Item>
                      <Form.Item
                        label="Token"
                        name={[tags[selectedTagIndex].name, "token"]}
                        style={{ margin: 0, flex: "1" }}
                      >
                        <Input placeholder="{{ExampleMergeTag}}" />
                      </Form.Item>
                    </div>

                    <Form.List name={[tags[selectedTagIndex].name, "entries"]}>
                      {(entries, { add: addEntry, remove: removeEntry, move: moveEntry }) => (
                        <>
                          <DynamicTagsEntries
                            form={form}
                            items={entries}
                            onMove={(from, to) => {
                              moveEntry(from, to);
                            }}
                            removeEntry={removeEntry}
                            selectedTagIndex={selectedTagIndex}
                          />
                          <Button
                            type="dashed"
                            onClick={() => addEntry()}
                            icon={<PlusOutlined />}
                            style={{ width: "100%" }}
                          >
                            Add Entry
                          </Button>
                        </>
                      )}
                    </Form.List>
                  </div>
                )}
              </Modal>

              <Modal
                title="Add New Tag"
                open={isAddTagModalVisible}
                onOk={() => {
                  if (tempTagName.trim()) {
                    addTag({
                      name: tempTagName,
                      token: tempTagName,
                      entries: [],
                    });
                    createTag({
                      variables: {
                        createInput: {
                          name: tempTagName,
                          token: tempTagName,
                        },
                      },
                      update: (cache, { data }) => {
                        if (data?.createDynamicTag) {
                          cache.modify({
                            fields: {
                              dynamicTags: (existingData) => [
                                ...existingData,
                                data.createDynamicTag,
                              ],
                            },
                          });
                        }
                      },
                    });
                    setTempTagName("");
                    setIsAddTagModalVisible(false);
                    const newTagIndex = tags.length ? tags.length : 0;
                    setSelectedTagIndex(newTagIndex);
                    handleTagClick(newTagIndex);
                  }
                }}
                onCancel={() => {
                  setTempTagName("");
                  setIsAddTagModalVisible(false);
                }}
              >
                <Input
                  placeholder="Name"
                  value={tempTagName}
                  onChange={(e) => setTempTagName(e.target.value)}
                />
              </Modal>
            </>
          )}
        </Form.List>
      </Form>
    </div>
  );
};
